import { useState, useRef, useCallback } from "react"
import { Upload, FileSpreadsheet } from "lucide-react"
import { Button } from "@/components/ui/button"

interface StepUploadProps {
  onUpload: (file: File, entityType: string) => void
  isLoading: boolean
  defaultEntityType?: "companies" | "contacts"
}

const ENTITY_TYPE_OPTIONS = [
  { value: "companies" as const, label: "Kontrahenci" },
  { value: "contacts" as const, label: "Kontakty" },
]

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export function StepUpload({ onUpload, isLoading, defaultEntityType }: StepUploadProps) {
  const [entityType, setEntityType] = useState(defaultEntityType || "companies")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFile = useCallback((file: File): string | null => {
    if (!file.name.endsWith(".csv")) {
      return "Dozwolone sa tylko pliki CSV (.csv)"
    }
    if (file.size > MAX_FILE_SIZE) {
      return "Plik jest za duzy (maksymalnie 10MB)"
    }
    return null
  }, [])

  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        setSelectedFile(null)
        return
      }
      setError(null)
      setSelectedFile(file)
    },
    [validateFile]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFileSelect(file)
    },
    [handleFileSelect]
  )

  const handleSubmit = useCallback(() => {
    if (selectedFile) {
      onUpload(selectedFile, entityType)
    }
  }, [selectedFile, entityType, onUpload])

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="space-y-4">
      {/* Entity type selector */}
      <div>
        <label className="block text-sm font-medium mb-2">Typ danych</label>
        <div className="flex gap-2">
          {ENTITY_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setEntityType(opt.value)}
              className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                entityType === opt.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-input text-muted-foreground hover:border-primary/50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Dropzone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 cursor-pointer transition-colors ${
          dragOver
            ? "border-primary bg-primary/5"
            : selectedFile
              ? "border-primary/50 bg-primary/5"
              : "border-input hover:border-primary/50"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleInputChange}
          className="hidden"
        />

        {selectedFile ? (
          <>
            <FileSpreadsheet className="w-10 h-10 text-primary" />
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                {selectedFile.name}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </>
        ) : (
          <>
            <Upload className="w-10 h-10 text-muted-foreground" />
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-primary">Kliknij</span> lub
                przeciagnij plik CSV
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Maksymalny rozmiar: 10MB
              </p>
            </div>
          </>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Submit */}
      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={!selectedFile || isLoading}
        >
          {isLoading ? "Przetwarzanie..." : "Dalej"}
        </Button>
      </div>
    </div>
  )
}
